import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2, ExternalLink, AlertTriangle, ShieldCheck } from "lucide-react";

import { api } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqList from "@/components/FaqList";
import { BreadcrumbJsonLd, FaqJsonLd, OrganizationJsonLd, ServiceJsonLd } from "@/components/Schemas";

export default function CheckBrand() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://checkexpert.be";

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/seo-pages/check/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="py-20 text-center text-ink-soft font-modern">Check niet gevonden. <Link to="/checks" className="text-[#002FA7] underline">Alle checks</Link></div>;
  if (!data) return <div className="py-16 text-center text-ink-soft font-modern">Laden…</div>;

  const { checker, content, intent_link_label, theme_slug } = data;
  const breadcrumbs = [
    { name: "Alle checks", url: "/checks" },
    { name: checker.display_name || checker.name, url: `/checks/${slug}` },
  ];

  return (
    <div className="bg-background font-modern" data-testid="check-brand">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.meta_description} />
        <link rel="canonical" href={`${origin}/checks/${slug}`} />
        <link rel="alternate" hrefLang="nl-BE" href={`${origin}/checks/${slug}`} />
        <link rel="alternate" hrefLang="nl-NL" href={`${origin}/checks/${slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`${origin}/checks/${slug}`} />
      </Helmet>
      <OrganizationJsonLd origin={origin} />
      <BreadcrumbJsonLd items={breadcrumbs} origin={origin} />
      <ServiceJsonLd name={checker.display_name || checker.name} description={content.brand_intro} slug={slug} origin={origin} />
      <FaqJsonLd faqs={content.faqs} />

      <div className="max-w-5xl mx-auto px-6 pt-10 pb-20">
        <Breadcrumbs items={breadcrumbs} />

        {/* HERO — answer-first */}
        <header className="mt-6">
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#002FA7]">
            {checker.category_label}
          </span>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl font-medium text-ink mt-3 leading-[1.05]" style={{ letterSpacing: "-0.025em" }}>
            {data.h1}
          </h1>
          <p className="text-ink-soft text-lg mt-5 max-w-2xl leading-relaxed">{content.brand_intro}</p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/check/${slug}/analyse`)}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold shadow-lg shadow-blue-600/20 transition-all hover:shadow-xl"
              data-testid="brand-cta-start"
            >
              Start je {checker.display_name || checker.name} — €4,99 <ArrowRight className="h-4 w-4" />
            </button>
            {theme_slug && (
              <Link to={`/${theme_slug}`} className="text-sm font-semibold text-ink-soft hover:text-ink underline-offset-4 hover:underline" data-testid="brand-link-intent">
                {intent_link_label} →
              </Link>
            )}
          </div>
          <p className="text-xs text-ink-subtle mt-5">Laatst bijgewerkt: {data.last_updated} · Onafhankelijk · GDPR-conform</p>
        </header>

        {/* WAT CONTROLEREN WE */}
        <section className="mt-14" data-testid="brand-what-checks">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Wat {checker.display_name || checker.name} concreet controleert
          </h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-3">
            {content.what_checks.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 rounded-xl bg-white border border-border p-4">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-ink leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* COMMON MISTAKES */}
        <section className="mt-14" data-testid="brand-mistakes">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Veelvoorkomende fouten en risico's
          </h2>
          <p className="text-ink-soft mt-3 max-w-2xl leading-relaxed">
            Dit zijn de meest geziene problemen in dit type document. {checker.display_name || checker.name} markeert ze met een ernstklasse (kritiek, hoog, aandacht) en linkt waar mogelijk naar het concrete wetsartikel.
          </p>
          <div className="mt-5 space-y-2.5">
            {content.common_mistakes.map((m, i) => (
              <div key={i} className="flex items-start gap-2.5 rounded-xl bg-amber-50/40 border border-amber-100 p-4">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-ink leading-relaxed">{m}</span>
              </div>
            ))}
          </div>
        </section>

        {/* WETGEVING */}
        <section className="mt-14" data-testid="brand-laws">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Belgische en Nederlandse wetgeving
          </h2>
          <p className="text-ink-soft mt-3 max-w-2xl leading-relaxed">
            {checker.display_name || checker.name} toetst je document tegen de volgende wettelijke kaders. Per bevinding linken we naar het relevante artikel.
          </p>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <LawBlock title="🇧🇪 België" laws={content.laws_be} />
            <LawBlock title="🇳🇱 Nederland" laws={content.laws_nl} />
          </div>
        </section>

        {/* VOORBEELDRAPPORT */}
        <section className="mt-14" data-testid="brand-sample">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Zo ziet je rapport eruit
          </h2>
          <div className="mt-6 rounded-2xl bg-white border border-border overflow-hidden shadow-sm">
            <div className="px-6 py-5 bg-gradient-to-br from-white to-blue-50/40 grid grid-cols-[auto_1fr] gap-5 items-center border-b border-slate-100">
              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-wider text-ink-soft font-bold">Score</span>
                <div className="font-editorial text-5xl font-medium text-[#002FA7] leading-none mt-1" style={{ letterSpacing: "-0.04em" }}>72</div>
                <span className="text-[10px] text-ink-soft mt-1">van 100</span>
              </div>
              <div>
                <div className="text-xs text-ink-soft uppercase tracking-wider font-bold mb-1.5">Resultaat (voorbeeld)</div>
                <div className="text-base font-semibold text-ink">Acceptabel, met 3 aandachtspunten</div>
                <p className="text-xs text-ink-soft mt-1.5">{checker.tagline}</p>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {checker.sample_findings.slice(0, 3).map((f, i) => (
                <li key={i} className="px-6 py-3 text-sm text-ink flex items-start gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-14" data-testid="brand-faq">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Veelgestelde vragen
          </h2>
          <FaqList faqs={content.faqs} />
        </section>

        {/* OFFICIËLE BRONNEN */}
        <section className="mt-14" data-testid="brand-sources">
          <h2 className="font-editorial text-2xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Officiële bronnen
          </h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2">
            {content.official_sources.map((s, i) => (
              <li key={i}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#002FA7] hover:underline underline-offset-4">
                  <ExternalLink className="h-3.5 w-3.5" /> {s.label}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA + disclaimer */}
        <section className="mt-14 rounded-3xl bg-gradient-to-br from-[#002FA7] to-[#001a5c] text-white p-10 sm:p-12 text-center" data-testid="brand-cta-bottom">
          <ShieldCheck className="h-9 w-9 mx-auto text-blue-200" />
          <h2 className="font-editorial text-3xl sm:text-4xl font-medium mt-4" style={{ letterSpacing: "-0.025em" }}>
            Laat je document doorlichten met {checker.display_name || checker.name}
          </h2>
          <p className="text-blue-100 mt-3 max-w-xl mx-auto">€4,99 nu, mogelijk honderden euro's bespaard. Resultaat in 60 seconden.</p>
          <button
            type="button"
            onClick={() => navigate(`/check/${slug}/analyse`)}
            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-ink font-semibold hover:bg-blue-50 transition-colors"
            data-testid="brand-bottom-cta"
          >
            Start je analyse <ArrowRight className="h-4 w-4" />
          </button>
        </section>

        <p className="text-[11px] text-ink-subtle text-center mt-8 max-w-2xl mx-auto leading-relaxed">
          Uitgever: Webtron BV (BE0793944703), Waregem (BE). CheckExpert vervangt geen juridisch, financieel of medisch advies. Bij twijfel: raadpleeg een specialist.
        </p>
      </div>
    </div>
  );
}

function LawBlock({ title, laws }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5">
      <h3 className="font-editorial text-base font-semibold text-ink mb-3">{title}</h3>
      <ul className="space-y-3">
        {laws.map((law, i) => (
          <li key={i} className="text-sm">
            <a href={law.source_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#002FA7] hover:underline underline-offset-4">
              {law.ref} <ExternalLink className="h-3 w-3 inline-block ml-0.5 mb-0.5" />
            </a>
            <p className="text-ink-soft text-[13px] mt-1 leading-relaxed">{law.summary}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
